import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Edit, Trash2, Truck } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import ConfirmPopup from './ui/ConfirmPopup';

interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address?: string;
  paymentTerms?: string;
  notes?: string;
  createdAt: string;
}

interface SupplierManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SupplierManagementModal: React.FC<SupplierManagementModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState<string | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  // Formulario para nuevo proveedor
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    paymentTerms: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen && user) {
      loadSuppliers();
    }
  }, [isOpen, user]);

  // Manejar tecla ESC para cerrar modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseModal();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleCloseModal = () => {
    // Resetear estado al cerrar
    setShowAddForm(false);
    setEditingSupplier(null);
    setFormData({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      paymentTerms: '',
      notes: ''
    });
    onClose();
  };

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      // Cargar proveedores desde el servicio de inventario
      const inventoryService = (await import('../services/inventoryService')).default;
      const suppliersData = await inventoryService.getSuppliersFromDocumentedStructure();
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error cargando proveedores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!user || !formData.name) return;
    
    try {
      if (editingSupplier) {
        // Editar proveedor existente
        const updatedSupplier: Supplier = {
          ...editingSupplier,
          name: formData.name,
          contactPerson: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          paymentTerms: formData.paymentTerms,
          notes: formData.notes
        };

        setSuppliers(prev => prev.map(s => s.id === editingSupplier.id ? updatedSupplier : s));
        setEditingSupplier(null);
        console.log('✅ Proveedor editado:', updatedSupplier.name);
      } else {
        // Agregar nuevo proveedor usando optimizedInventoryService
        const optimizedService = (await import('../services/optimizedInventoryService')).default;
        const supplierId = await optimizedService.createSupplier({
          name: formData.name,
          contact: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          address: formData.address
        });

        const newSupplier: Supplier = {
          id: supplierId,
          name: formData.name,
          contactPerson: formData.contactPerson,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          paymentTerms: formData.paymentTerms,
          notes: formData.notes,
          createdAt: new Date().toISOString().split('T')[0]
        };

        setSuppliers(prev => [...prev, newSupplier]);
        console.log('✅ Proveedor creado en Firebase:', newSupplier.name, 'ID:', supplierId);
      }

      setFormData({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        paymentTerms: '',
        notes: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error agregando/editando proveedor:', error);
    }
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contactPerson: supplier.contactPerson,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address || '',
      paymentTerms: supplier.paymentTerms || '',
      notes: supplier.notes || ''
    });
    setShowAddForm(true);
  };

  const handleCancelEdit = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: '',
      paymentTerms: '',
      notes: ''
    });
    setShowAddForm(false);
  };

  const handleDeleteSupplier = (supplierId: string) => {
    setSupplierToDelete(supplierId);
    setShowConfirmPopup(true);
  };

  const handleConfirmDelete = async () => {
    if (!supplierToDelete) return;
    
    try {
      // Eliminar de Firebase usando inventoryService
      const inventoryService = (await import('../services/inventoryService')).default;
      await inventoryService.deleteSupplier(supplierToDelete);
      
      // Eliminar del estado local
      setSuppliers(prev => prev.filter(s => s.id !== supplierToDelete));
      console.log(`✅ Proveedor eliminado de Firebase: ${supplierToDelete}`);
    } catch (error) {
      console.error('Error eliminando proveedor:', error);
    } finally {
      setSupplierToDelete(null);
      setShowConfirmPopup(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`fixed inset-4 z-50 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            } rounded-2xl shadow-2xl overflow-hidden`}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <Truck className={`${isDark ? 'text-blue-400' : 'text-blue-600'}`} size={24} />
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('supplierManagement')}
                  </h2>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-xl transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {!showAddForm ? (
                  // Vista principal
                  <div className="h-full flex flex-col">
                    {/* Header con controles */}
                    <div className={`p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className={`text-lg font-semibold ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {t('mySuppliers')}
                          </h3>
                          <p className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {t('manageSuppliersAndContacts')}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            console.log('🔄 Abriendo formulario de Nuevo Proveedor');
                            setShowAddForm(true);
                          }}
                          className="btn-primary flex items-center space-x-2 px-6 py-2"
                        >
                          <Plus size={20} />
                          <span>{t('newSupplier')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Lista de proveedores */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {loading ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                      ) : suppliers.length === 0 ? (
                        <div className="text-center py-12">
                          <Truck className={`mx-auto mb-4 ${
                            isDark ? 'text-gray-600' : 'text-gray-400'
                          }`} size={48} />
                          <p className={`text-lg ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {t('noSuppliersRegistered')}
                          </p>
                          <p className={`text-sm mt-2 ${
                            isDark ? 'text-gray-500' : 'text-gray-500'
                          }`}>
                            {t('addSuppliersToManage')}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {suppliers.map((supplier) => (
                            <div
                              key={supplier.id}
                              className={`p-6 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-700 border-slate-600' 
                                  : 'bg-white border-gray-200'
                              } hover:shadow-lg transition-shadow duration-200`}
                            >
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h3 className={`text-lg font-semibold ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {supplier.name}
                                  </h3>
                                  <p className={`text-sm mt-1 ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    {supplier.contactPerson}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-2 mb-4">
                                <p className={`text-sm ${
                                  isDark ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  <span className="font-medium">{t('email')}</span> {supplier.email}
                                </p>
                                <p className={`text-sm ${
                                  isDark ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  <span className="font-medium">{t('phone')}</span> {supplier.phone}
                                </p>
                                {supplier.address && (
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">{t('address')}</span> {supplier.address}
                                  </p>
                                )}
                                {supplier.paymentTerms && (
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">Términos:</span> {supplier.paymentTerms}
                                  </p>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEditSupplier(supplier)}
                                  className={`p-2 rounded-lg transition-colors duration-200 ${
                                    isDark 
                                      ? 'hover:bg-slate-700 text-gray-400 hover:text-white' 
                                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteSupplier(supplier.id)}
                                  className={`p-2 rounded-lg transition-colors duration-200 ${
                                    isDark 
                                      ? 'hover:bg-slate-700 text-gray-400 hover:text-red-400' 
                                      : 'hover:bg-gray-100 text-gray-600 hover:text-red-600'
                                  }`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Formulario de agregar/editar proveedor
                  <div className="h-full flex flex-col">
                    {/* Header del formulario */}
                    <div className={`p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <h3 className={`text-xl font-bold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {editingSupplier ? t('editSupplier') : t('newSupplier')}
                        </h3>
                        <button
                          onClick={handleCancelEdit}
                          className={`p-2 rounded-xl transition-colors duration-200 ${
                            isDark 
                              ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                              : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>

                    {/* Contenido del formulario */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-6">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Nombre del Proveedor *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('supplierNamePlaceholder')}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Persona de Contacto *
                          </label>
                          <input
                            type="text"
                            value={formData.contactPerson}
                            onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e.target.value }))}
                            placeholder={t('contactPersonPlaceholder')}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Email *
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="contacto@proveedor.com"
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {t('phone')} *
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="+54 11 1234-5678"
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {t('address')}
                          </label>
                          <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                            placeholder={t('addressPlaceholder')}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Términos de Pago
                          </label>
                          <input
                            type="text"
                            value={formData.paymentTerms}
                            onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                            placeholder="30 días"
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Notas
                          </label>
                          <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder={t('additionalInfoPlaceholder')}
                            rows={3}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Footer del formulario */}
                    <div className={`p-6 border-t ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-end space-x-4">
                        <button
                          onClick={handleCancelEdit}
                          className="btn-secondary px-6 py-2"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          onClick={handleAddSupplier}
                          className="btn-primary px-6 py-2"
                        >
                          {editingSupplier ? t('saveSupplier') : t('addSupplier')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Popup */}
              <ConfirmPopup
                isOpen={showConfirmPopup}
                onClose={() => {
                  setShowConfirmPopup(false);
                  setSupplierToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t('deleteSupplier')}
                message="¿Estás seguro de que quieres eliminar este proveedor?\n\nEsta acción no se puede deshacer."
                type="danger"
                confirmText={t('delete')}
                cancelText={t('cancel')}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SupplierManagementModal;